'use client'

import { rememberMyLocale } from '@/lib/actions/account'
import { useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Globe, ChevronDown } from 'lucide-react'
import { useClickOutside } from '@/lib/utils/use-click-outside'
import { LOCALE_COOKIE } from '@/lib/i18n/config'

// Флаги и самоназвания языков — намеренные литералы (см. конвенции i18n).
const LOCALES = [
  { code: 'ru', label: 'RU' },
  { code: 'ro', label: 'RO' },
  { code: 'en', label: 'EN' },
  { code: 'uk', label: 'UKR' },
] as const

/**
 * Компактный переключатель языка для страниц ВНЕ шелла (auth и т.п.).
 *
 * Дропдаун (глобус + код языка), как MobileLocaleSwitcher на лендинге, —
 * ряд из четырёх кнопок не помещался в шапке auth и выталкивал «На главную».
 *
 * Меняет локаль-префикс в URL, СОХРАНЯЯ путь и query-параметры — критично для
 * приглашённых: ссылка из письма несёт ?invite=…&email=…, и смена языка не
 * должна их терять. Плюс пишет куку NEXT_LOCALE, чтобы middleware запомнил
 * выбор для следующих заходов. (Поэтому НЕ через Link locale={…} — тот ведёт
 * на фиксированный href и теряет query.)
 */
// keepPath — для страниц без языкового префикса (каталог, портал перевозчика):
// адрес не меняется, меняется только выбор в cookie, и страница собирается
// заново на сервере уже на новом языке — без вспышки старого текста.
export function LanguageSwitcher({ keepPath = false }: { keepPath?: boolean } = {}) {
  const locale = useLocale()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useClickOutside(ref, () => setOpen(false), { active: open })

  function switchTo(code: string) {
    setOpen(false)
    if (code === locale) return
    try {
      document.cookie = `${LOCALE_COOKIE}=${code}; path=/; max-age=31536000; samesite=lax`
    } catch {}
    // 909: выбор — и в учётную запись: письма уходят с сервера, когда браузер
    // закрыт. Гостю сохранять нечего — действие это знает само.
    void rememberMyLocale(code)
    if (keepPath) {
      router.refresh()
      return
    }
    const rest = pathname.replace(/^\/(ru|ro|en|uk)(?=\/|$)/, '')
    const qs = searchParams.toString()
    // Плановая навигация plain-роутером: путь строится С локалью вручную
    router.push(`/${code}${rest || '/'}${qs ? `?${qs}` : ''}`)
  }

  const current = LOCALES.find(l => l.code === locale) ?? LOCALES[0]

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg border-hairline border-ink-200 bg-white text-[12.5px] font-semibold text-ink-700 hover:text-ink-900 transition-colors"
        aria-label={t('shell.languageSwitcher')}
        aria-expanded={open}
      >
        <Globe size={14} strokeWidth={1.8} />
        {current.label}
        <ChevronDown
          size={12}
          strokeWidth={2}
          className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 min-w-[110px] rounded-lg border-hairline border-ink-200 bg-white shadow-md py-1 z-50">
          {LOCALES.map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => switchTo(l.code)}
              className={
                'flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-left transition-colors ' +
                (l.code === locale
                  ? 'text-ink-900 font-bold bg-ink-50'
                  : 'text-ink-600 font-medium hover:bg-ink-50')
              }
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
